import React from 'react';

interface UserData {
  phone: string;
  countryCode: string;
  testType: 'english' | 'math';
  testLanguage: 'english' | 'arabic';
  studyLocation: string;
  nationality: string;
  studyField: string;
  grade: string;
  schoolType: 'private' | 'government';
  schoolName: string;
}

interface QuizAnswer {
  questionId: number;
  selectedOption: number;
  isCorrect: boolean;
}

interface ResultsStepProps {
  userData: UserData;
  quizAnswers: QuizAnswer[];
  isRTL: boolean;
}

const ResultsStep: React.FC<ResultsStepProps> = ({ userData, quizAnswers, isRTL }) => {
  const totalQuestions = 20;
  const correctAnswers = quizAnswers.filter(answer => answer.isCorrect).length;
  const score = Math.round((correctAnswers / totalQuestions) * 100);

  const services = [
    {
      title: isRTL ? 'البحث عن أفضل الجامعات' : 'Find the Best Universities',
      description: isRTL ? 'نساعدك في العثور على الجامعات المناسبة لتخصصك' : 'We help you find universities suitable for your major',
      icon: '🎓'
    },
    {
      title: isRTL ? 'المساعدة في الحصول على القبول الجامعي' : 'University Admission Assistance',
      description: isRTL ? 'دعم شامل في عملية التقديم والقبول' : 'Comprehensive support in application and admission process',
      icon: '📝'
    },
    {
      title: isRTL ? 'الاستشارات الأكاديمية' : 'Academic Consultations',
      description: isRTL ? 'استشارات مجانية من خبراء التعليم' : 'Free consultations from education experts',
      icon: '💡'
    },
    {
      title: isRTL ? 'التحضير للاختبارات' : 'Test Preparation',
      description: isRTL ? 'برامج تدريبية للاختبارات المعيارية' : 'Training programs for standardized tests',
      icon: '📚'
    }
  ];

  const universities = [
    'Harvard University', 'MIT', 'Stanford University', 'Oxford University',
    'Cambridge University', 'ETH Zurich', 'University of Toronto', 'ANU'
  ];

  const faqs = [
    {
      question: isRTL ? 'كيف يمكنني التقديم للجامعات؟' : 'How can I apply to universities?',
      answer: isRTL ? 'نقدم لك الدعم الكامل في عملية التقديم من البداية حتى النهاية' : 'We provide complete support in the application process from start to finish'
    },
    {
      question: isRTL ? 'ما هي تكلفة الخدمات؟' : 'What is the cost of services?',
      answer: isRTL ? 'نقدم استشارات مجانية والعديد من الخدمات بأسعار تنافسية' : 'We offer free consultations and many services at competitive prices'
    },
    {
      question: isRTL ? 'هل تساعدون في الحصول على المنح؟' : 'Do you help with scholarships?',
      answer: isRTL ? 'نعم، نساعدك في العثور على المنح المناسبة والتقديم عليها' : 'Yes, we help you find suitable scholarships and apply for them'
    }
  ];

  return (
    <div className="results-container">
      {/* Score Card */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body text-center p-4">
          <h1 className="fw-bold fs-4 mb-3" style={{ color: '#2c3e50' }}>
            لقد أنهيت الاختبار الوطني التجريبي
          </h1>
          
          <div className="score-circle mx-auto mb-3" style={{
            width: '120px',
            height: '120px',
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #3498db, #2980b9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 25px rgba(52, 152, 219, 0.3)'
          }}>
            <span className="text-white fw-bold" style={{ fontSize: '2.5rem' }}>
              {score}
            </span>
          </div>
          
          <p className="text-muted mb-3">
            {correctAnswers}/{totalQuestions} - {userData.testType === 'english' ? 'قسم اللغة الإنجليزية' : 'قسم الرياضيات'}
          </p>
          
          <button className="btn btn-primary w-100 rounded-pill btn-lg">
            التقديم على الجامعات عبر Ulearn
          </button>
        </div>
      </div>

      {/* Services Section */}
      <div className="mb-4">
        <h3 className="fw-bold fs-5 text-center mb-4" style={{ color: '#2c3e50' }}>
          {isRTL ? 'ما هي خدماتنا؟' : 'What are our services?'}
        </h3>
        
        <div className="row g-3">
          {services.map((service, index) => (
            <div key={index} className="col-12 col-md-6">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-3 text-center">
                  <div className="mb-2" style={{ fontSize: '2rem' }}>
                    {service.icon}
                  </div>
                  <h5 className="fw-bold fs-6 mb-2">{service.title}</h5>
                  <p className="text-muted small mb-0">{service.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Partner Universities */}
      <div className="mb-4">
        <h3 className="fw-bold fs-5 text-center mb-4" style={{ color: '#2c3e50' }}>
          {isRTL ? 'شركاؤنا من الجامعات' : 'Our University Partners'}
        </h3>
        
        <div className="row g-2">
          {universities.map((university, index) => (
            <div key={index} className="col-6 col-md-3">
              <div className="card border-0 shadow-sm">
                <div className="card-body p-2 text-center">
                  <div className="bg-light rounded p-3 mb-2" style={{ height: '60px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <small className="fw-semibold text-muted">{university}</small>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ Section */}
      <div className="mb-4">
        <h3 className="fw-bold fs-5 text-center mb-4" style={{ color: '#2c3e50' }}>
          {isRTL ? 'أسئلة شائعة' : 'Frequently Asked Questions'}
        </h3>
        
        <div className="accordion" id="faqAccordion">
          {faqs.map((faq, index) => (
            <div key={index} className="accordion-item border-0 shadow-sm mb-2">
              <h2 className="accordion-header">
                <button 
                  className="accordion-button collapsed rounded"
                  type="button" 
                  data-bs-toggle="collapse" 
                  data-bs-target={`#faq${index}`}
                >
                  {faq.question}
                </button>
              </h2>
              <div 
                id={`faq${index}`} 
                className="accordion-collapse collapse" 
                data-bs-parent="#faqAccordion"
              >
                <div className="accordion-body">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <div className="card border-0 shadow-sm" style={{ background: '#2c3e50', color: 'white' }}>
        <div className="card-body p-4">
          <div className="row g-4">
            <div className="col-12 col-md-4">
              <h5 className="fw-bold mb-3">Ulearn</h5>
              <p className="small mb-0">
                {isRTL ? 'منصتك المثالية للتعليم العالي' : 'Your ideal platform for higher education'}
              </p>
            </div>
            <div className="col-12 col-md-4">
              <h6 className="fw-bold mb-3">
                {isRTL ? 'روابط سريعة' : 'Quick Links'}
              </h6>
              <ul className="list-unstyled small">
                <li className="mb-1">
                  <a href="#" className="text-light text-decoration-none">
                    {isRTL ? 'الخدمات' : 'Services'}
                  </a>
                </li>
                <li className="mb-1">
                  <a href="#" className="text-light text-decoration-none">
                    {isRTL ? 'الجامعات' : 'Universities'}
                  </a>
                </li>
                <li className="mb-1">
                  <a href="#" className="text-light text-decoration-none">
                    {isRTL ? 'اتصل بنا' : 'Contact Us'}
                  </a>
                </li>
              </ul>
            </div>
            <div className="col-12 col-md-4">
              <h6 className="fw-bold mb-3">
                {isRTL ? 'تواصل معنا' : 'Contact Us'}
              </h6>
              <div className="d-flex gap-3">
                <a href="#" className="text-light">
                  <i className="bi bi-facebook fs-5"></i>
                </a>
                <a href="#" className="text-light">
                  <i className="bi bi-twitter fs-5"></i>
                </a>
                <a href="#" className="text-light">
                  <i className="bi bi-instagram fs-5"></i>
                </a>
                <a href="#" className="text-light">
                  <i className="bi bi-linkedin fs-5"></i>
                </a>
              </div>
              <div className="mt-3">
                <small>
                  <i className="bi bi-envelope me-2"></i>
                  info@ulearn.com
                </small>
                <br />
                <small>
                  <i className="bi bi-phone me-2"></i>
                  +965 1234 5678
                </small>
              </div>
            </div>
          </div>
          
          <hr className="my-4" />
          
          <div className="text-center">
            <small>
              © 2024 Ulearn. {isRTL ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
            </small>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResultsStep;
